import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styles from './contactform.module.css';
import modalStyles from '../../styles/modal.module.css';

// List of standard trades - matching SubBidForm
const STANDARD_TRADES = [
  "Concrete",
  "Demolition",
  "Drywall",
  "Electrical",
  "Excavation",
  "Fire Protection",
  "Flooring",
  "Framing",
  "HVAC",
  "Insulation",
  "Masonry",
  "Painting",
  "Plumbing",
  "Roofing",
  "Steel"
];

function ContactForm({ onSubmit, onCancel, defaultValues, type }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Check if it's a subcontractor form
  const isSubcontractor = type === 'subcontractors';
  
  // Check if the default Trade is a non-standard one (for subcontractors)
  const isNonStandardTrade = isSubcontractor && defaultValues?.trade && !STANDARD_TRADES.includes(defaultValues.trade);
  
  // Prepare default values
  const formDefaultValues = {
    // Use different field names for subcontractors
    name: defaultValues?.name || '',
    email: defaultValues?.email || '',
    phone: defaultValues?.phone || '',
    address: defaultValues?.address || '',
    // Trade fields only for subcontractors
    trade: isNonStandardTrade ? 'Other' : (defaultValues?.trade || ''),
    customTrade: isNonStandardTrade ? defaultValues?.trade : '',
  };
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: formDefaultValues
  });

  // Watch the Trade field for subcontractors
  const selectedTrade = watch('trade');
  const [trade, setTrade] = useState(formDefaultValues.trade);
  
  // Update local state when trade changes
  useEffect(() => {
    if (isSubcontractor) {
      setTrade(selectedTrade);
    }
  }, [selectedTrade, isSubcontractor]);

  const onFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      let contactData;
      if (isSubcontractor) {
        // Format data for subcontractors including trade
        contactData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          trade: data.trade === 'Other' ? data.customTrade : data.trade,
          type: 'subcontractor',
        };
      } else {
        // Format data for clients
        contactData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          type: 'client',
        };
      }
      console.log('ContactForm: Submitting form with data:', contactData);
      await onSubmit(contactData);
    } catch (error) {
      console.error('ContactForm: Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={modalStyles.modalContent}>
      <h3 className={styles.formTitle}>
        {defaultValues ? `Edit ${isSubcontractor ? 'Subcontractor' : 'Client'}` : `Add New ${isSubcontractor ? 'Subcontractor' : 'Client'}`}
      </h3>
      
      <form onSubmit={handleSubmit(onFormSubmit)} className={styles.form}>
        {/* Trade dropdown only for subcontractors */}
        {isSubcontractor && (
          <>
            <div className={modalStyles.formGroup}>
              <label>Trade</label>
              <select 
                {...register('trade', { required: 'Trade is required' })}
              >
                <option value="">Select Trade</option>
                {STANDARD_TRADES.map(trade => (
                  <option key={trade} value={trade}>{trade}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {errors.trade && <span className={styles.error}>{errors.trade.message}</span>}
            </div>
            
            {trade === 'Other' && (
              <div className={modalStyles.formGroup}>
                <label>Custom Trade</label>
                <input 
                  {...register('customTrade', { 
                    required: trade === 'Other' ? 'Custom trade is required' : false 
                  })} 
                  placeholder="Enter custom trade" 
                />
                {errors.customTrade && <span className={styles.error}>{errors.customTrade.message}</span>}
              </div>
            )}
          </>
        )}
        
        <div className={modalStyles.formGroup}>
          <label>{isSubcontractor ? 'Subcontractor Name' : 'Name'}</label>
          <input 
            {...register('name', { required: 'Name is required' })} 
            placeholder={isSubcontractor ? "e.g., Power Rod" : "e.g., John Doe"} 
          />
          {errors.name && <span className={styles.error}>{errors.name.message}</span>}
        </div>
        
        <div className={modalStyles.formGroup}>
          <label>Email</label>
          <input 
            type="email" 
            {...register('email', { 
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })} 
            placeholder="e.g., john@abccorp.com" 
          />
          {errors.email && <span className={styles.error}>{errors.email.message}</span>}
        </div>
        
        <div className={modalStyles.formGroup}>
          <label>Phone</label>
          <input 
            {...register('phone')} 
            placeholder="e.g., 123-456-7890" 
          />
          {errors.phone && <span className={styles.error}>{errors.phone.message}</span>}
        </div>
        
        <div className={modalStyles.formGroup}>
          <label>Address</label>
          <input 
            {...register('address')} 
            placeholder="e.g., 123 Main St, City, State" 
          />
          {errors.address && <span className={styles.error}>{errors.address.message}</span>}
        </div>
        
        <div className={modalStyles.formActions}>
          <button 
            type="submit" 
            className={modalStyles.primaryButton}
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (defaultValues ? 'Updating...' : 'Creating...') 
              : (defaultValues ? 'Update' : 'Create')
            }
          </button>
          <button 
            type="button" 
            onClick={onCancel} 
            className={modalStyles.secondaryButton}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ContactForm; 